import { Badge, Card, Table, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { ErrorState, LoadingState } from "@how/ui";
import { supabase } from "../../lib/supabase";

interface Growth {
  readonly table: string;
  readonly measuredOn: string | null;
  readonly rows: number | null;
  readonly bytes: number | null;
  readonly rows30DaysAgo: number | null;
  readonly act: boolean;
}

const number = new Intl.NumberFormat("en-US");
function size(bytes: number | null): string {
  if (bytes === null) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i] ?? "B"}`;
}

/**
 * The tables that grow with use (issue 64, ADR 0049): today's rows and size, the change over thirty days, and which
 * table is past the line to partition or prune. Measured once a day; a platform super admin's view.
 */
export function TableGrowthCard() {
  const growth = useQuery({
    queryKey: ["platform-table-growth"],
    queryFn: async (): Promise<Growth[]> => {
      const { data, error } = await supabase.rpc("platform_table_growth");
      if (error) throw error;
      return data as unknown as Growth[];
    },
  });
  return (
    <Card mt="lg">
      <Title order={4} mb="xs">
        Tables that grow
      </Title>
      <Text size="sm" c="dimmed" mb="md">
        Measured daily. A table past its line is due to be partitioned or pruned (ADR 0049); pruning
        waits for counsel&apos;s retention answer.
      </Text>
      {growth.isPending ? (
        <LoadingState rows={4} />
      ) : growth.isError ? (
        <ErrorState message={growth.error.message} onRetry={() => void growth.refetch()} />
      ) : (
        <Table.ScrollContainer minWidth={560}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Table</Table.Th>
                <Table.Th className="how-numeric">Rows</Table.Th>
                <Table.Th className="how-numeric">In 30 days</Table.Th>
                <Table.Th className="how-numeric">Size</Table.Th>
                <Table.Th>State</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {growth.data.map((g) => (
                <Table.Tr key={g.table}>
                  <Table.Td>{g.table}</Table.Td>
                  <Table.Td className="how-numeric">
                    {g.rows === null ? "—" : number.format(g.rows)}
                  </Table.Td>
                  <Table.Td className="how-numeric">
                    {g.rows === null || g.rows30DaysAgo === null
                      ? "—"
                      : `${g.rows >= g.rows30DaysAgo ? "+" : ""}${number.format(g.rows - g.rows30DaysAgo)}`}
                  </Table.Td>
                  <Table.Td className="how-numeric">{size(g.bytes)}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={g.act ? "red" : "gray"}>
                      {g.act ? "Act" : "Watch"}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Card>
  );
}
